/**
 * TopicList container
 * Left side topic list; Common Question is on the top for home page followed by topics list
 * Users can access leftside topic list from article, topic, and home page
 */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Link } from '@paypalcorp/pp-react';
import i18n from '../../lib/reacti18n';
import Page from '../../lib/page';
import { trackLink } from '../../lib/analytics-helper';
import { getPageUrlInfoFromLocation } from '../../lib/location-helper';
import urlResource from '../constants/UrlResources';
import routeDidUpdate from '../actions/actions-router';
import { guideClicked } from '../actions/action-icf-page';
import { isNewExpLayoutEnabled } from '../../../jsV2/helpers/twHelper';

import getHistory from '../../lib/HistoryHelper';
import {
  updateSelectedArticle,
  updateL1TopicDetails,
  updateSelectedSubTopicDetails,
} from '../actions/action-L3Topic-list';
import { TOPIC } from '../constants/PropTypesShapes';
import { redirectToHomePage } from '../../../jsV2/helpers/globalHelper';

function TopicList(props) {
  const {
    location,
    topics,
    isLoading,
    error,
    article,
    breadcrumb,
    blacklistedMarketICFFlag,
    SelectedL2TopicDetails,
    L3Articles,
    SelectedL3Article,
    isL3TopicClicked,
    SelectedL1TopicDetails,
  } = props;
  const FPTIId = Page.getFPTIPageId();

  const popular = {
    common: i18n.message('smarthelp/home.home.common'),
    recommended: i18n.message('smarthelp/home.home.recommended'),
    home: i18n.message('smarthelp/home.home.home'),
    payPalGuide: i18n.message('smarthelp/home.home.payPalGuide'),
    payPalGuideDescription: i18n.message('smarthelp/home.home.guideDescription'),
  };
  const pg = getPageUrlInfoFromLocation(location);

  const popularClass = classNames({
    'topic-tree-elements': true,
    'topic-selected': pg.resource.name === urlResource.HOME,
  });

  const parentTopic = (currentArticle, breadcrumbs) => {
    if (Array.isArray(breadcrumbs) && breadcrumbs[1]) {
      return breadcrumbs[1].id;
    }
    if (currentArticle && currentArticle.breadCrumbPath) {
      const topicTree = currentArticle.breadCrumbPath.split('/');
      return topicTree[1];
    }
    return null;
  };

  // FPTI tracking users' click behavior based on the triggered page
  const goToHome = () => {
    trackLink({ id: FPTIId, link: 'smarthelp-hcr-topic-list|common-questions' });
    redirectToHomePage();
  };

  const goToTopicList = (e, topic) => {
    e.preventDefault();
    if (isL3TopicClicked && SelectedL1TopicDetails && SelectedL1TopicDetails.topicId) {
      trackLink({
        id: 'L2topicClicked',
        link: `smarthelp-hcr-topic-list|${SelectedL1TopicDetails.topicId}|${topic.topicId}`,
      });
      props.updateSelectedSubTopicDetails(topic, topics);
      getHistory().push(`/topic/${SelectedL1TopicDetails.topicId}`);
    } else {
      trackLink({ id: 'L1topicClicked', link: `smarthelp-hcr-topic-list|${topic.topicId}` });
      props.updateL1TopicDetails(topic, topics);
      getHistory().push(`/topic/${topic.topicId}`);
    }
  };

  const getTopicClass = (topic) => {
    if (pg.resource.name === urlResource.HOME || Page.isMobileLayout()) {
      return 'topic-tree-elements';
    }
    if (pg.resource.name === urlResource.TOPIC && String(topic.topicId) === pg.resource.id) {
      return 'topic-tree-elements topic-selected';
    }
    if (pg.resource.name === urlResource.ARTICLE && parentTopic(article, breadcrumb) === String(topic.topicId)) {
      return 'topic-tree-elements topic-selected';
    }
    if (topic.topicId && topic.topicId === SelectedL2TopicDetails.id) {
      return 'topic-tree-elements topic-selected';
    }
    if (topic.articleId && SelectedL3Article.articleId && topic.articleId === SelectedL3Article.articleId) {
      return 'topic-tree-elements topic-selected';
    }
    return 'topic-tree-elements';
  };

  const getRecommendedPopularLabel = () => {
    if (Page.isLoggedIn()) {
      return popular.home;
    }
    return popular.common;
  };

  const getCommonQuestions = () => (
    <li className="topic-tree-elements-item topic-tree-elements-item__common-question">
      <Link theme="v2" className={popularClass} onClick={() => goToHome()}>
        {getRecommendedPopularLabel()}
      </Link>
    </li>
  );

  const payPalGuideClick = (e) => {
    e.preventDefault();
    const payPalGuide = urlResource.PAYPALGUIDE;
    trackLink({ id: 'homePage', link: `smarthelp-hcr-topic-list|${payPalGuide}` });
    props.guideClicked(false);
    props.routeDidUpdate({ pathname: `/${payPalGuide}` });
    getHistory().push(`/${payPalGuide}`);
  };

  const payPalHelpGuides = () => {
    // if remote config is disabled or not technical experience
    if (Page.isTechnicalExp() || blacklistedMarketICFFlag || isNewExpLayoutEnabled()) {
      return null;
    }

    return (
      <li className="topic-tree-elements-item">
        <Link
          theme="v2" // eslint-disable-line jsx-a11y/no-static-element-interactions
          className="topic-tree-elements"
          onClick={(e) => payPalGuideClick(e)}
          href={`/${Page.getCountry()}/smarthelp/${urlResource.PAYPALGUIDE}`}
        >
          <div className="paypal-guide-header">{popular.payPalGuide}</div>
        </Link>
      </li>
    );
  };

  const updateArticleList = (faq) => {
    trackLink({ id: 'sideBarArticleClicked', link: `smarthelp-hcr-article-list|${faq.articleId}` });
    props.updateSelectedArticle(faq);
  };

  const getTopicList = () => {
    if (pg.resource.name === urlResource.ARTICLE && Array.isArray(L3Articles) && L3Articles.length) {
      // Rendering articles in the side bar after clicking on articles under L3 Subtopic
      return L3Articles.map((faq) => (
        <li key={faq.articleId} className="topic-tree-elements-item">
          <Link
            theme="v2"
            className={getTopicClass(faq)}
            key={faq.articleId}
            href={`/${Page.getCountry()}/smarthelp/article/${faq.articleId}`}
            onClick={() => updateArticleList(faq)}
          >
            <div className="topic-tree-home-content">{faq.articleTitle}</div>
          </Link>
        </li>
      ));
    }
    return topics.map((topic) => {
      if (topic.topicId && topic.topicName) {
        let path = `/${Page.getCountry()}/smarthelp/topic/${topic.topicId}`;
        if (SelectedL1TopicDetails && SelectedL1TopicDetails.topicId) {
          path = `/${Page.getCountry()}/smarthelp/topic/${SelectedL1TopicDetails.topicId}`;
        }
        return (
          <li key={topic.topicId} className="topic-tree-elements-item">
            <Link
              theme="v2"
              className={getTopicClass(topic)}
              key={topic.topicId}
              href={path}
              onClick={(e) => goToTopicList(e, topic)}
            >
              <div className="topic-tree-home-content">{topic.topicName}</div>
            </Link>
          </li>
        );
      }
      return null;
    });
  };

  const renderTopicTree = () => {
    if (!isLoading && !error) {
      if (
        isL3TopicClicked &&
        ((Array.isArray(SelectedL2TopicDetails) && SelectedL2TopicDetails.subTopicList) || Page.isArticlePage())
      ) {
        // Do not render PayPalHelpGuides and common questions label when clicked on L3 Topic
        return (
          <nav>
            <ul className="topic-tree-container-home">{getTopicList()}</ul>
          </nav>
        );
      }
      return (
        <nav>
          <ul className="topic-tree-container-home">
            {getCommonQuestions()}
            {payPalHelpGuides()}
            {getTopicList()}
          </ul>
        </nav>
      );
    }
    return null;
  };
  return renderTopicTree();
}

function mapStateToProps(state) {
  return {
    isLoading: state.topicList.isLoading,
    topics: state.topicList.topics,
    location: state.location,
    error: state.topicList.error,
    article: state.article.article,
    breadcrumb: state.breadcrumb.items,
    blacklistedMarketICFFlag: state.topicList.blacklistedMarketICFFlag,
    SelectedL2TopicDetails: state.L3TopicList.SelectedL2TopicDetails,
    L3Articles: state.L3TopicList.L3Articles,
    SelectedL3Article: state.L3TopicList.SelectedL3Article,
    isL3TopicClicked: state.L3TopicList.isL3TopicClicked,
    SelectedL1TopicDetails: state.L3TopicList.SelectedL1TopicDetails,
  };
}
function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      routeDidUpdate,
      guideClicked,
      updateL1TopicDetails,
      updateSelectedArticle,
      updateSelectedSubTopicDetails,
    },
    dispatch,
  );
}
TopicList.propTypes = {
  topics: PropTypes.arrayOf(
    PropTypes.shape({
      topicId: PropTypes.string,
      topicName: PropTypes.string,
    }),
  ),
  blacklistedMarketICFFlag: PropTypes.bool,
  isLoading: PropTypes.bool,
  error: PropTypes.bool,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }),
  routeDidUpdate: PropTypes.func.isRequired,
  guideClicked: PropTypes.func.isRequired,
  isL3TopicSelected: PropTypes.bool,
  SelectedL2TopicDetails: PropTypes.shape(TOPIC).isRequired,
  SelectedL1TopicDetails: PropTypes.shape(TOPIC).isRequired,
  updateSelectedSubTopicDetails: PropTypes.func.isRequired,
};

TopicList.defaultProps = {
  topics: [],
  display: false,
  location: undefined,
  isL3TopicSelected: false,
  SelectedL2TopicDetails: {},
};
export default connect(mapStateToProps, mapDispatchToProps)(TopicList);
